import React, { useState, useRef, useEffect } from 'react';
import '../../TaskManagement.css';

// Use the feedback-app backend as proxy to avoid CORS issues
// Empty string means same-origin (for nginx proxy), only fallback to localhost if undefined
const API_BASE = process.env.REACT_APP_API_BASE !== undefined ? process.env.REACT_APP_API_BASE : 'http://localhost:3001';

// Idle timeout: redirect to previous page if no PIN activity within this duration
const IDLE_TIMEOUT_MS = 1 * 60 * 1000; // 1 minute — change multiplier to adjust

function StaffPinEntry({ onPinValidated, onBackHome, tenantId, customerId }) {
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [isLocked, setIsLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const abortControllerRef = useRef(null);
  const idleTimerRef = useRef(null);

  // Start/reset the idle timer — called on mount and on every digit input
  const resetIdleTimer = (backHome) => {
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      backHome();
    }, IDLE_TIMEOUT_MS);
  };

  // Start idle timer on mount; clear everything on unmount
  useEffect(() => {
    resetIdleTimer(onBackHome);
    return () => {
      clearTimeout(idleTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(0, 1);
    const newPin = [...pin];
    newPin[index] = digit;
    setPin(newPin);
    setError('');
    resetIdleTimer(onBackHome); // reset idle timer on any input activity

    // Auto-focus next input
    if (digit && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace on empty field - go to previous
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const pinString = pin.join('');
    if (pinString.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    if (isLocked) {
      setError('Service Desk is locked out. Please try again later or contact Admin.');
      return;
    }

    if (!tenantId || !customerId) {
      setError('Missing tenant or customer information. Please scan QR code again.');
      return;
    }

    setIsLoading(true);
    setError('');

    // Abort any previous request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      // PIN in a POST body, so it never lands in proxy access logs as a URL.
      // A server from before the POST route answers 404/405 — fall back to the
      // old GET form so a newer APK still works against it.
      const signal = abortControllerRef.current.signal;
      let response = await fetch(`${API_BASE}/api/staff-auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, customerId, pin: pinString }),
        signal,
      });
      if (response.status === 404 || response.status === 405) {
        response = await fetch(
          `${API_BASE}/api/staff-auth?tenantId=${encodeURIComponent(tenantId)}&customerId=${encodeURIComponent(customerId)}&pin=${encodeURIComponent(pinString)}`,
          { signal }
        );
      }

      if (!response.ok && response.status !== 403) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Check for success (200 OK with staff data)
      if (response.ok && data.staff) {
        // Extract bearer token and refresh token from response headers
        const authHeader = response.headers.get('Authorization');
        const bearerToken = authHeader ? authHeader.replace('Bearer ', '') : null;
        const refreshToken = response.headers.get('X-TB-Refresh-Token');

        // Pass staff data and tokens to parent
        onPinValidated({
          ...data.staff,
          bearerToken,
          refreshToken,
        });
      } else {
        // Authentication failed (403 or other error)
        const newAttemptsLeft = attemptsLeft - 1;
        setAttemptsLeft(newAttemptsLeft);
        setPin(['', '', '', '']);
        inputRefs[0].current?.focus();

        if (newAttemptsLeft <= 0) {
          setIsLocked(true);
          setError(
            'Service Desk is locked out due to max # of consecutive unsuccessful PIN attempts. Please try again later or contact Admin.'
          );
        } else {
          setError(
            `Entered PIN is not found. Please try again. ${newAttemptsLeft} attempt${newAttemptsLeft > 1 ? 's' : ''} remaining, after which Service Desk will get locked out.`
          );
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Staff auth error:', err);
      setError('Unable to verify PIN. Please try again.');
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="task-auth-container">
      <button className="auth-back-btn" onClick={onBackHome} aria-label="Back to home">
        🏠
      </button>
      <div className="task-auth-card">
        <h2 className="task-auth-title">Enter Staff PIN to start Task Management</h2>

        {error && (
          <div className="task-auth-error" role="alert">
            {error}
          </div>
        )}

        {!isLocked && (
          <form onSubmit={handleSubmit}>
            <div className="pin-input-container">
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  ref={inputRefs[index]}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={pin[index]}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="pin-input-box"
                  autoFocus={index === 0}
                  placeholder="_"
                  aria-label={`PIN digit ${index + 1}`}
                />
              ))}
            </div>

            <button
              type="submit"
              className="pin-submit-btn"
              disabled={pin.join('').length !== 4 || isLoading}
            >
              {isLoading ? 'Verifying...' : 'Submit'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default StaffPinEntry;
